import { Formik } from 'formik'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'
import isBefore from 'date-fns/isBefore'
import addDays from 'date-fns/addDays'
import startOfDay from 'date-fns/startOfDay'
import endOfDay from 'date-fns/endOfDay'

import { Button } from 'components/v2/Button'
import { Input } from 'components/Input'
import { Textarea } from 'components/v2/Textarea'
import { Select } from 'components/v2/Select'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Row } from '@10xLabs/react-web/components/Row'
import { Column } from '@10xLabs/react-web/components/Column'
import { InputDateTime } from 'components/InputDateTime'
import FormLayout from 'features/App/components/FormLayout'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { campaignAreaMessages, campaignTypeMessages } from 'features/Campaigns/components/CampaignType/messages'
import { schema } from './schema'
import { getUnprocessableErrors } from 'utils/error'
import { sortByTranslation } from 'utils/intl'
import { TIMEZONES, TIMEZONE } from 'utils/timezones'
import { GTCampaignArea, GTCampaignType } from 'gql/types.generated'
import { canSelectCompany, getDefaultCompanyForForm } from 'features/Login/utils'
import { useLogin } from 'features/Login/hooks'

const messages = defineMessages({
  companyPlaceholder: {
    id: 'companies.autocomplete.placeholder',
    defaultMessage: 'Select company'
  },
  campaignAreaPlaceholder: {
    id: 'campaigns.form.area.placeholder',
    defaultMessage: 'Select area'
  },
  campaignTypePlaceholder: {
    id: 'campaigns.form.type.placeholder',
    defaultMessage: 'Select type'
  },
  namePlaceholder: {
    id: 'dictionary.name',
    defaultMessage: 'Name'
  }
})

const getCampaingAreasList = (campaignArea: GTCampaignArea, isEdit: boolean): GTCampaignArea[] => {
  if (isEdit && campaignArea === GTCampaignArea.Snacks) {
    return [GTCampaignArea.Snacks]
  }

  return [
    GTCampaignArea.Marketing,
    GTCampaignArea.CustomerSupport,
    GTCampaignArea.Snacks,
    GTCampaignArea.Partners,
    GTCampaignArea.Travel,
    GTCampaignArea.Mobile,
    GTCampaignArea.Sales
  ]
}
const getCampaingTypesList = (): GTCampaignType[] => {
  return Object.values(GTCampaignType).filter(
    /*TODO: Remove filter when deprecated options are removed*/
    (type) =>
      type !== GTCampaignType.Snacks && type !== GTCampaignType.Marketing && type !== GTCampaignType.CustomerSupport
  )
}
const NewCampaignForm = (props: TSFixMe): TSFixMe => {
  const {
    values,
    touched,
    errors,
    handleChange,
    setFieldValue,
    handleBlur,
    handleSubmit,
    error,
    loading,
    isEdit,
    initialValues
  } = props

  const intl = useIntl()
  const login = useLogin()

  const handleCompanyChange = (value: TSFixMe): TSFixMe => {
    setFieldValue('company', value)
  }

  const campaignsAreasList = getCampaingAreasList(values.area, isEdit).sort((a, b) =>
    sortByTranslation(a, b, intl, campaignAreaMessages)
  )
  const campaignTypesList = getCampaingTypesList().sort((a, b) => sortByTranslation(a, b, intl, campaignTypeMessages))

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <FormLayout.CardContent>
        <Row>
          {canSelectCompany(login.accessToken) && (
            <Column width="50%">
              <CompanyAutocomplete
                id="company.id"
                onChange={handleCompanyChange}
                onBlur={handleBlur}
                value={values.company.name}
                placeholder={intl.formatMessage(messages.companyPlaceholder)}
                touched={touched.company && touched.company.id}
                errors={errors.company && errors.company.id}
                label={<FormattedMessage id="dictionary.company" defaultMessage="Company" />}
                disabled={isEdit}
              />
            </Column>
          )}
          <Column width="50%">
            <Input
              id="name"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              placeholder={intl.formatMessage(messages.namePlaceholder)}
              touched={touched.name}
              errors={errors.name}
              label={<FormattedMessage id="dictionary.name" defaultMessage="Name" />}
            />
          </Column>
        </Row>

        <Row>
          <Column width="50%">
            <Select
              id="type"
              value={values.type}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.type}
              errors={errors.type}
              label={<FormattedMessage id="campaigns.form.type.label" defaultMessage="Type" />}
            >
              <option value="">{intl.formatMessage(messages.campaignTypePlaceholder)}</option>

              {campaignTypesList.map((type) => {
                if (!campaignTypeMessages[type]) return null
                return (
                  <option key={type} value={type}>
                    {intl.formatMessage(campaignTypeMessages[type])}
                  </option>
                )
              })}
            </Select>
          </Column>

          <Column width="50%">
            <Select
              id="area"
              value={values.area}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.area}
              errors={errors.area}
              label={<FormattedMessage id="campaigns.form.area.label" defaultMessage="Area" />}
              disabled={isEdit && initialValues.area === GTCampaignArea.Snacks}
            >
              <option value="">{intl.formatMessage(messages.campaignAreaPlaceholder)}</option>

              {campaignsAreasList.map((area: TSFixMe): TSFixMe => {
                return (
                  <option key={area} value={area}>
                    {intl.formatMessage(campaignAreaMessages[area as GTCampaignArea])}
                  </option>
                )
              })}
            </Select>
          </Column>
        </Row>

        <Row>
          <Column width="50%">
            <InputDateTime
              id="startsAt"
              intl={intl}
              value={values.startsAt}
              onChange={(value): TSFixMe => {
                setFieldValue('startsAt', value)
                /*@ts-expect-error ts(2345)*/
                if (values.endsAt && isBefore(values.endsAt, value)) {
                  setFieldValue('endsAt', value)
                }
              }}
              disabledDays={{
                before: new Date()
              }}
              touched={touched.startsAt}
              errors={errors.startsAt}
              label={<FormattedMessage id="services.form.startsAt.label" defaultMessage="Starting date" />}
            />
          </Column>

          <Column width="50%">
            <InputDateTime
              id="endsAt"
              intl={intl}
              value={values.endsAt}
              onChange={(value): TSFixMe => {
                setFieldValue('endsAt', value)
                /*@ts-expect-error ts(2345)*/
                if (isBefore(value, values.startsAt)) {
                  setFieldValue('startsAt', value)
                }
              }}
              disabledDays={{
                before: new Date()
              }}
              touched={touched.endsAt}
              errors={errors.endsAt}
              label={<FormattedMessage id="services.form.endsAt.label" defaultMessage="Ending date" />}
            />
          </Column>
        </Row>

        <Row>
          <Column width="50%">
            <Select
              id="timezone"
              value={values.timezone}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.timezone}
              errors={errors.timezone}
              label={<FormattedMessage id="dictionary.timezone" defaultMessage="Timezone" />}
            >
              {TIMEZONES.map((timezone: TSFixMe): TSFixMe => {
                return (
                  <option key={timezone.value} value={timezone.value}>
                    {timezone.label}
                  </option>
                )
              })}
            </Select>
          </Column>
        </Row>

        <Row>
          <Column>
            <Textarea
              id="description"
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.description}
              errors={errors.description}
              label={<FormattedMessage id="campaigns.form.description.label" defaultMessage="Description" />}
            />
          </Column>
        </Row>
      </FormLayout.CardContent>

      <FormLayout.Split />

      <FormLayout.CardFooter>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="campaigns.form.error"
                defaultMessage="There was an error while creating your campaign"
              />
            </ErrorMessage>
          </div>
        )}

        <FormLayout.CardFooterActions>
          <Button as={NavLink} to="/campaigns" kind="cancel">
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>

          <Button type="submit" kind="primary" loading={loading}>
            {isEdit ? (
              <FormattedMessage id="dictionary.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="dictionary.create" defaultMessage="Create" />
            )}
          </Button>
        </FormLayout.CardFooterActions>
      </FormLayout.CardFooter>
    </form>
  )
}

export const NewCampaign = ({ campaign = {}, onSubmit, error, loading, isEdit }: TSFixMe): TSFixMe => {
  const intl = useIntl()
  const login = useLogin()

  const tomorrow = addDays(new Date(), 1)

  const initialCampaign = {
    company: campaign.company || getDefaultCompanyForForm(login.accessToken),
    name: campaign.name || '',
    area: campaign.area || '',
    type: campaign.type || '',
    startsAt: campaign.startsAt ? new Date(campaign.startsAt) : startOfDay(tomorrow),
    endsAt: campaign.endsAt ? new Date(campaign.endsAt) : endOfDay(tomorrow),
    timezone: campaign.timezone || TIMEZONE.MEXICO_CITY,
    description: campaign.description || ''
  }

  return (
    <Formik initialValues={initialCampaign} validationSchema={schema(intl)} onSubmit={onSubmit}>
      {(props): TSFixMe => {
        return <NewCampaignForm {...props} isEdit={isEdit} error={error} loading={loading} />
      }}
    </Formik>
  )
}
